import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Banknote,
  CheckCircle2,
  Loader2,
  XCircle,
  RefreshCw,
  Filter,
  Landmark,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '@/api/payments.api';
import { hgcashApi } from '@/api/hgcash.api';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/retiros')({
  component: RetirosPage,
});

interface Payment {
  id: string;
  clientId: string;
  clientName?: string;
  type: 'deposit' | 'withdrawal';
  amount: string;
  currency: string;
  // 'processing' | 'paying_out' | 'paid_out': estados del payout automático SmartCobro
  // (pending -> processing -> paying_out -> paid_out). 'processing' + un marker en `notes`
  // ("payout_attention:<motivo>") = quedó trabado y necesita ojo humano (ver isAttention()).
  // 'resolved_manual': cierre manual de un retiro trabado (F3) -- no re-acredita nada, es la
  // constancia de que un operador ya verificó en SmartCobro y resolvió por afuera.
  status:
    | 'pending'
    | 'processing'
    | 'approved'
    | 'paying_out'
    | 'paid_out'
    | 'rejected'
    | 'reversed'
    | 'resolved_manual';
  method?: string;
  bankAccountId?: string;
  notes?: string | null;
  rejectedReason?: string | null;
  externalTxId?: string | null;
  // Destino/titular estructurados (Task 3/4 del payout automático). El backend YA los persiste en
  // la DB pero, al momento de este cambio, `PaymentsService.toDto()` todavía no los expone en la
  // respuesta del admin (archivo en edición por otro subagente en paralelo) -- por eso son
  // opcionales acá y el front cae a `parseWithdrawNotes` como fallback mientras tanto.
  payoutDestination?: string | null;
  payoutHolder?: string | null;
  payoutVerified?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/** Saca destino y titular del notes del retiro ("CBU/Alias: X · Titular: Y"). Fallback para
 * retiros viejos o mientras `payoutDestination`/`payoutHolder` no vengan en la respuesta. */
function parseWithdrawNotes(notes?: string | null): { destino: string; titular: string } {
  const destino = notes?.match(/CBU\/Alias:\s*([^·\n]+)/)?.[1]?.trim() ?? '';
  const titular = notes?.match(/Titular:\s*([^|\n]+)/)?.[1]?.trim() ?? '';
  return { destino: destino === '-' ? '' : destino, titular: titular === '-' ? '' : titular };
}

/** Marker que `flagAttention()` (payments.service.ts) agrega a `notes` cuando un retiro con
 * payout automático queda trabado (verify falló, saldo insuficiente en Maira, candado tomado,
 * error de transfer, webhook rejected/failed, etc). No hay status dedicado -- el payment sigue en
 * 'processing' -- así que la única forma de detectarlo hoy es este marker + el evento de socket
 * `payment.attention` (mismo patrón que emite el backend). */
const ATTENTION_MARK = 'payout_attention:';
/** Estados en los que el retiro ya está cerrado: el marker de atención queda en `notes` como
 *  historial, pero ya no hay nada que atender (F3/F4 -- si no, un retiro rechazado o resuelto a
 *  mano seguiría contando en el card de "Atención" para siempre). */
const TERMINAL_STATUSES = ['paid_out', 'rejected', 'reversed', 'resolved_manual'];
function isAttention(p: Payment): boolean {
  return !!p.notes && p.notes.includes(ATTENTION_MARK) && !TERMINAL_STATUSES.includes(p.status);
}

/**
 * 🔴 F3 — Espejo EXACTO de `PRE_DEBIT_ATTENTION_REASONS` en el backend
 * (apps/backend/src/modules/payments/payments.service.ts). Motivos en los que el retiro se trabó
 * ANTES del débito in-game: el jugador conserva su saldo, así que se puede rechazar normalmente.
 * Esto es SÓLO para decidir qué botón mostrar -- la regla real la impone el backend en `reject()`.
 * Allowlist fail-closed: lo que no está acá se cierra con "Marcar resuelto".
 * 🚨 Si se agrega un motivo nuevo allá, agregarlo acá también (o el operador ve un botón de menos).
 */
const PRE_DEBIT_ATTENTION_REASONS = [
  'payout_sin_destino_estructurado',
  'payout_destino_no_verificado',
  'payout_verify_error',
  'payout_balance_error',
  'payout_saldo_insuficiente_maira',
];
function allAttentionReasons(notes?: string | null): string[] {
  if (!notes) return [];
  const matches = notes.match(new RegExp(`${ATTENTION_MARK}[^\\s|]+`, 'g'));
  return (matches ?? []).map((m) => m.slice(ATTENTION_MARK.length));
}
/** true si TODOS los motivos acumulados son pre-débito (si acumuló uno post-débito, no se rechaza). */
function isPreDebitAttention(notes?: string | null): boolean {
  const reasons = allAttentionReasons(notes);
  return reasons.length > 0 && reasons.every((r) => PRE_DEBIT_ATTENTION_REASONS.includes(r));
}
function attentionReason(notes?: string | null): string | null {
  if (!notes) return null;
  // Varios markers pueden acumularse en notes (flagAttention los concatena con ' | ' en cada
  // intento fallido nuevo). Con flag global tomamos TODOS y nos quedamos con el ÚLTIMO -- el
  // motivo vigente, no el más viejo. Si notes viene truncado (max 2000 chars) y corta un marker
  // a la mitad, ese match incompleto no matchea (requiere 1+ char tras el ':') y cae al anterior
  // completo -- comportamiento razonable, sin romper.
  const reasons = allAttentionReasons(notes);
  if (reasons.length === 0) return null;
  const last = reasons[reasons.length - 1];
  return reasons.length > 1 ? `${last} (${reasons.length})` : last;
}
/** Retiro ya gestionado (o en curso) por el payout automático SmartCobro -- para NO ofrecer el
 * botón de payout manual de HG Cash encima (evita un doble pago). */
function isSmartCobroManaged(p: Payment): boolean {
  return p.status === 'paying_out' || p.status === 'paid_out';
}

/**
 * Port antifraude ganamos→combatwin (2026-07-27) -- score/flags que
 * `PaymentsService.shouldAutoApproveWithdrawal` calcula ANTES de decidir el auto-payout (gate:
 * `Settings.data.autoPayoutMaxRiskScore`, default 70) y que `ChatGateway.onWithdrawRequest` deja
 * en `notes` (mismo canal append-only que `ATTENTION_MARK`/`auto_aprobado_sistema` de acá arriba
 * -- espejo EXACTO de `RISK_SCORE_MARK`/`RISK_FLAGS_MARK`/`parseRiskFromNotes` en
 * apps/backend/src/modules/payments/payments.service.ts). SÓLO aparece en retiros donde el
 * scoring efectivamente corrió (tenant con auto-payout ON, bajo monto/límite diario) -- el resto
 * de los retiros (los "otros 5 tenants", o cualquiera sobre el tope) no tienen este marker.
 */
const RISK_SCORE_MARK = 'antifraud_score:';
const RISK_FLAGS_MARK = 'antifraud_flags:';
function parseRiskFromNotes(notes?: string | null): { score: number; flags: string[] } | null {
  if (!notes) return null;
  const scoreMatch = notes.match(new RegExp(`${RISK_SCORE_MARK}(-?\\d+)`));
  if (!scoreMatch) return null;
  const flagsMatch = notes.match(new RegExp(`${RISK_FLAGS_MARK}([^|]*)`));
  const flags = flagsMatch ? flagsMatch[1].split(';').map((s) => s.trim()).filter(Boolean) : [];
  return { score: Number(scoreMatch[1]), flags };
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  processing: 'Procesando',
  approved: 'Aprobado',
  paying_out: 'Pagando (SmartCobro)',
  paid_out: 'Pagado ✅',
  rejected: 'Rechazado',
  reversed: 'Revertido',
  resolved_manual: 'Resuelto a mano',
};

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  processing: 'secondary',
  approved: 'default',
  paying_out: 'default',
  paid_out: 'default',
  rejected: 'destructive',
  reversed: 'destructive',
  resolved_manual: 'outline',
};

function RetirosPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [payoutPayment, setPayoutPayment] = useState<Payment | null>(null);
  const [payoutDestino, setPayoutDestino] = useState('');
  const [payoutTipo, setPayoutTipo] = useState<'cbu' | 'cvu'>('cbu');
  const [payoutTitular, setPayoutTitular] = useState('');
  const [search, setSearch] = useState('');
  // F3: diálogo de cierre manual de un retiro trabado (no re-acredita nada, ver resolveMutation).
  const [resolvePayment, setResolvePayment] = useState<Payment | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  // Filtro "atención": no es un status del backend (los retiros trabados siguen en 'processing'
  // + marker en notes), así que cuando está activo ignoramos statusFilter y traemos todo.
  const [attentionOnly, setAttentionOnly] = useState(false);

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['payments', 'withdrawal', statusFilter, attentionOnly],
    queryFn: async () => {
      const effectiveStatus = attentionOnly ? undefined : (statusFilter === 'all' ? undefined : statusFilter);
      const res = await paymentsApi.list({ type: 'withdrawal', status: effectiveStatus });
      // el backend devuelve el array directo (kind→type via paymentsApi); soporta ambos shapes
      const rows = Array.isArray(res) ? res : (res?.data ?? []);
      return rows as Payment[];
    },
  });

  // Query separada (siempre "todos los retiros") para poder mostrar un conteo de "Atención"
  // confiable en el stat card, sin depender de qué status esté filtrado en la tabla principal.
  const { data: allWithdrawalsForAttention = [] } = useQuery({
    queryKey: ['payments', 'withdrawal', 'attention-count'],
    queryFn: async () => {
      const res = await paymentsApi.list({ type: 'withdrawal' });
      const rows = Array.isArray(res) ? res : (res?.data ?? []);
      return rows as Payment[];
    },
    refetchInterval: 30_000,
  });
  const attentionCount = useMemo(
    () => allWithdrawalsForAttention.filter(isAttention).length,
    [allWithdrawalsForAttention],
  );

  const { data: hgConfig } = useQuery({
    queryKey: ['hgcash', 'config'],
    queryFn: hgcashApi.config,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const payoutMutation = useMutation({
    mutationFn: (p: { paymentId: string; toCBU?: string; toCVU?: string; toName?: string }) =>
      hgcashApi.payout(p),
    onSuccess: (res) => {
      toast.success(`Transferencia enviada a HG Cash (${res.status ?? 'PENDING'}) — se avisa al jugador cuando acredite`);
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setPayoutPayment(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || 'HG Cash rechazó la transferencia');
    },
  });

  const openPayout = (p: Payment) => {
    const parsed = parseWithdrawNotes(p.notes);
    setPayoutDestino(parsed.destino.replace(/\D/g, '').length === 22 ? parsed.destino.replace(/\D/g, '') : parsed.destino);
    setPayoutTitular(parsed.titular);
    setPayoutTipo('cbu');
    setPayoutPayment(p);
  };

  const submitPayout = () => {
    if (!payoutPayment) return;
    const destino = payoutDestino.replace(/\D/g, '');
    if (!/^\d{22}$/.test(destino)) {
      toast.error('El destino debe ser un CBU/CVU de 22 dígitos (si el jugador dio un alias, resolvelo primero)');
      return;
    }
    payoutMutation.mutate({
      paymentId: payoutPayment.id,
      ...(payoutTipo === 'cbu' ? { toCBU: destino } : { toCVU: destino }),
      ...(payoutTitular ? { toName: payoutTitular } : {}),
    });
  };

  /**
   * 🟡 F5 — ANTES esto era `toast.success('Retiro aprobado')` incondicional sobre el 200. Pero
   * TODOS los caminos de atención del backend (`flagAttention`) devuelven 200 con el DTO: el
   * operador veía verde mientras el retiro quedaba trabado sin transferir un peso. Ahora se lee el
   * estado real del DTO devuelto y se avisa lo que efectivamente pasó.
   */
  const approveMutation = useMutation({
    mutationFn: paymentsApi.approve,
    onSuccess: (res: Payment | undefined) => {
      const reason = res ? attentionReason(res.notes) : null;
      if (res && isAttention(res)) {
        toast(
          `⚠️ El retiro NO se transfirió — quedó EN ATENCIÓN${reason ? ` (${reason})` : ''}. ` +
            'Revisalo en el filtro "⚠️ Atención" antes de tocar nada.',
          { icon: '⚠️', duration: 12000, style: { background: '#fef3c7', color: '#92400e' } },
        );
      } else if (res?.status === 'rejected') {
        toast.error(`Retiro rechazado: ${res.rejectedReason || 'el débito de fichas falló'}`);
      } else if (res?.status === 'paying_out') {
        toast.success('Transferencia enviada a SmartCobro — se confirma cuando el banco acredite');
      } else {
        toast.success('Retiro aprobado');
      }
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPayment(null);
      setActionType(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Error al aprobar');
    },
  });

  /**
   * 🔴 F3 — Cierre manual de un retiro trabado donde el jugador YA fue debitado (o hay una
   * transferencia posiblemente en vuelo). NO re-acredita nada: sólo deja constancia y saca el
   * retiro del limbo. El único otro camino de salida es "Rechazar", y sólo para los motivos
   * pre-débito (ver isPreDebitAttention).
   */
  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => paymentsApi.resolveAttention(id, note),
    onSuccess: () => {
      toast.success('Retiro cerrado manualmente — quedó registrado quién y por qué');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setResolvePayment(null);
      setResolveNote('');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || 'No se pudo cerrar el retiro');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => paymentsApi.reject(id, reason),
    onSuccess: () => {
      toast.success('Retiro rechazado');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPayment(null);
      setActionType(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Error al rechazar');
    },
  });

  const columns = useMemo<ColumnDef<Payment>[]>(
    () => [
      {
        accessorKey: 'clientName',
        header: 'Cliente',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.clientName || '—'}</div>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Monto',
        cell: ({ row }) => (
          <div className="font-mono font-medium">
            {formatCurrency(row.original.amount, row.original.currency)}
          </div>
        ),
      },
      {
        accessorKey: 'method',
        header: 'Método',
        cell: ({ row }) => (
          <span className="text-sm capitalize">{row.original.method || '—'}</span>
        ),
      },
      {
        id: 'payout',
        header: 'Destino / Titular',
        cell: ({ row }) => {
          const p = row.original;
          const parsed = parseWithdrawNotes(p.notes);
          const destino = p.payoutDestination || parsed.destino || '—';
          const titularReal = p.payoutHolder || null;
          const titular = titularReal || parsed.titular || '—';
          return (
            <div className="text-xs leading-tight max-w-[180px]">
              <div className="font-mono truncate" title={destino}>{destino}</div>
              <div className="text-muted-foreground truncate flex items-center gap-1" title={titular}>
                {titular}
                {titularReal && p.payoutVerified && (
                  <span className="text-green-600" title="Titular verificado por SmartCobro">✓</span>
                )}
                {!titularReal && parsed.titular && (
                  <span
                    className="text-amber-500"
                    title="Titular sin verificar por el PSP (dato tipeado por el jugador, puede no ser real)"
                  >
                    ⚠
                  </span>
                )}
              </div>
              {p.externalTxId && (
                <div
                  className="text-[10px] text-muted-foreground font-mono truncate"
                  title={`Referencia SmartCobro: ${p.externalTxId}`}
                >
                  Ref: {p.externalTxId}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => {
          const p = row.original;
          const attn = isAttention(p);
          const reason = attentionReason(p.notes);
          const risk = parseRiskFromNotes(p.notes);
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge variant={STATUS_VARIANTS[p.status] || 'outline'}>
                {STATUS_LABELS[p.status] || p.status}
              </Badge>
              {attn && (
                <Badge
                  variant="destructive"
                  className="gap-1 whitespace-nowrap"
                  title={reason ?? 'Retiro trabado -- requiere revisión manual'}
                >
                  ⚠️ Atención{reason ? `: ${reason}` : ''}
                </Badge>
              )}
              {risk && (
                <Badge
                  variant={risk.score >= 70 ? 'destructive' : risk.score > 0 ? 'secondary' : 'outline'}
                  className="gap-1 whitespace-nowrap"
                  title={
                    risk.flags.length > 0
                      ? `Score antifraude: ${risk.score}/100 — ${risk.flags.join(' · ')}`
                      : `Score antifraude: ${risk.score}/100 — sin señales de riesgo`
                  }
                >
                  🛡️ Riesgo {risk.score}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Solicitado',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleString('es-AR')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const p = row.original;
          // 🔴 F3 — PRIMERO los retiros trabados en atención: antes de este fix no mostraban NINGÚN
          // botón (el `if (p.status !== 'pending') return null` de abajo los dejaba sin salida) y
          // sólo se destrababan con SQL a mano. Va antes que las ramas de paying_out/paid_out porque
          // un `paying_out` con webhook `rejected` también necesita salida.
          if (isAttention(p)) {
            const preDebit = isPreDebitAttention(p.notes);
            return (
              <div className="flex items-center gap-2">
                {preDebit && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                    title="El jugador NUNCA fue debitado: se le devuelve el retiro con su saldo intacto."
                    onClick={() => {
                      setSelectedPayment(p);
                      setActionType('reject');
                    }}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Rechazar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                  title={
                    preDebit
                      ? 'Cerrar sin rechazar (queda registrado quién y por qué).'
                      : 'El jugador YA fue debitado o puede haber una transferencia en vuelo: verificá en SmartCobro antes de cerrar. No re-acredita fichas.'
                  }
                  onClick={() => {
                    setResolveNote('');
                    setResolvePayment(p);
                  }}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Marcar resuelto
                </Button>
              </div>
            );
          }
          // Payout automático SmartCobro en curso o ya confirmado: NO ofrecer el botón manual
          // de HG Cash acá (evita un doble pago -- ver flagAttention/processWithdrawalPayout).
          if (p.status === 'paying_out') {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                SmartCobro: en proceso
              </span>
            );
          }
          if (p.status === 'paid_out') {
            return <span className="text-xs text-green-600">✅ Pagado por SmartCobro</span>;
          }
          if (p.status === 'approved' && hgConfig?.enabled && !p.externalTxId && !isSmartCobroManaged(p)) {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => openPayout(p)}
              >
                <Landmark className="h-3.5 w-3.5" />
                Pagar por HG Cash
              </Button>
            );
          }
          if (p.status === 'approved' && p.externalTxId) {
            return <span className="text-xs text-muted-foreground">💸 HG enviado</span>;
          }
          if (p.status !== 'pending') return null;
          return (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-green-600 border-green-200 hover:bg-green-50"
                onClick={() => {
                  setSelectedPayment(p);
                  setActionType('approve');
                }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setSelectedPayment(p);
                  setActionType('reject');
                }}
              >
                <XCircle className="h-3.5 w-3.5" />
                Rechazar
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hgConfig?.enabled]
  );

  const pendingCount = payments.filter((p) => p.status === 'pending').length;

  return (
    <>
      <TopBanner
        title="Retiros"
        subtitle={`Solicitudes de retiro de fichas (${pendingCount} pendientes)`}
        searchValue={search}
        onSearchChange={setSearch}
      />
      <div className="flex-1 overflow-auto p-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <Banknote className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{pendingCount}</div>
                <div className="text-xs text-muted-foreground">Pendientes</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {payments.filter((p) => p.status === 'approved' || p.status === 'paid_out').length}
                </div>
                <div className="text-xs text-muted-foreground">Aprobados / pagados</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-red-100 p-2.5">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {payments.filter((p) => p.status === 'rejected').length}
                </div>
                <div className="text-xs text-muted-foreground">Rechazados hoy</div>
              </div>
            </CardContent>
          </Card>
          {/* Retiros con payout automático trabado (verify/saldo/candado/transfer/webhook fallido)
              -- sin status dedicado, se detectan por el marker que deja flagAttention() en notes.
              Card clickeable: alterna el filtro "solo atención" de la tabla de abajo. */}
          <Card
            role="button"
            tabIndex={0}
            onClick={() => setAttentionOnly((v) => !v)}
            className={`cursor-pointer transition-colors ${attentionOnly ? 'ring-2 ring-amber-500' : ''}`}
          >
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{attentionCount}</div>
                <div className="text-xs text-muted-foreground">⚠️ Atención (trabados)</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {(
            ['all', 'pending', 'processing', 'approved', 'paying_out', 'paid_out', 'rejected', 'resolved_manual'] as const
          ).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={!attentionOnly && statusFilter === s ? 'default' : 'outline'}
              onClick={() => {
                setAttentionOnly(false);
                setStatusFilter(s);
              }}
            >
              {s === 'all' ? 'Todos' : STATUS_LABELS[s]}
            </Button>
          ))}
          <Button
            size="sm"
            variant={attentionOnly ? 'destructive' : 'outline'}
            className={attentionOnly ? '' : 'text-amber-600 border-amber-300 hover:bg-amber-50'}
            onClick={() => setAttentionOnly((v) => !v)}
          >
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
            ⚠️ Atención{attentionCount > 0 ? ` (${attentionCount})` : ''}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['payments'] })}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Actualizar
          </Button>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={payments
            .filter((p) => !attentionOnly || isAttention(p))
            .filter((p) => (p.clientName ?? '').toLowerCase().includes(search.toLowerCase()))}
          isLoading={isLoading}
        />

        {/* Empty state */}
        {!isLoading && payments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Banknote className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-lg font-medium">No hay retiros</p>
            <p className="text-sm">Las solicitudes de retiro aparecerán acá.</p>
          </div>
        )}
      </div>

      {/* Approve/Reject Dialog */}
      <Dialog open={!!selectedPayment && !!actionType} onOpenChange={() => { setSelectedPayment(null); setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Aprobar retiro' : 'Rechazar retiro'}
            </DialogTitle>
            <DialogDescription>
              {selectedPayment && (
                <>
                  Cliente: <strong>{selectedPayment.clientName}</strong>
                  <br />
                  Monto: <strong>{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</strong>
                  {actionType === 'reject' && isAttention(selectedPayment) && (
                    <>
                      <br />
                      <span className="text-amber-600">
                        ⚠️ Este retiro se trabó ANTES de descontarle las fichas al jugador
                        {attentionReason(selectedPayment.notes)
                          ? ` (${attentionReason(selectedPayment.notes)})`
                          : ''}
                        . Al rechazarlo conserva su saldo intacto y recibe el aviso en el chat.
                      </span>
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedPayment(null); setActionType(null); }}>
              Cancelar
            </Button>
            {actionType === 'approve' ? (
              <Button
                onClick={() => selectedPayment && approveMutation.mutate(selectedPayment.id)}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar aprobación
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() =>
                  selectedPayment &&
                  rejectMutation.mutate({
                    id: selectedPayment.id,
                    reason: isAttention(selectedPayment)
                      ? 'No pudimos procesar la transferencia. Tus fichas NO fueron descontadas, podés volver a pedir el retiro.'
                      : 'Rechazado por operador',
                  })
                }
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar rechazo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F3 — Cierre manual de un retiro trabado en atención. NO mueve plata: saca el retiro del
          limbo y deja constancia de quién lo cerró y por qué. */}
      <Dialog open={!!resolvePayment} onOpenChange={(o) => !o && setResolvePayment(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>⚠️ Marcar retiro como resuelto</DialogTitle>
            <DialogDescription>
              {resolvePayment && (
                <>
                  {resolvePayment.clientName || 'Cliente'} ·{' '}
                  <strong>{formatCurrency(resolvePayment.amount, resolvePayment.currency)}</strong>
                  <br />
                  Motivo del trabe:{' '}
                  <strong>{attentionReason(resolvePayment.notes) || 'sin motivo registrado'}</strong>
                  {resolvePayment.externalTxId && (
                    <>
                      <br />
                      Ref. SmartCobro: <span className="font-mono">{resolvePayment.externalTxId}</span>
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {resolvePayment && !isPreDebitAttention(resolvePayment.notes) && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Antes de cerrar, verificá en SmartCobro</strong> (panel → Transferencias) si la
                transferencia salió o no.
                <ul className="mt-1 list-disc pl-4 space-y-0.5">
                  <li>Al jugador ya se le descontaron las fichas.</li>
                  <li>
                    Esto <strong>NO le devuelve las fichas</strong>: si la transferencia nunca salió,
                    devolvéselas a mano en el casino antes de cerrar.
                  </li>
                  <li>Cerrar sólo registra que ya lo resolviste — no manda ninguna plata.</li>
                </ul>
              </div>
            )}
            <div>
              <Input
                placeholder="Qué verificaste y qué hiciste (obligatorio)"
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Ej.: "Verifiqué en SmartCobro: la transferencia no salió. Devolví las 15.000 fichas a mano."
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolvePayment(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                resolvePayment &&
                resolveMutation.mutate({ id: resolvePayment.id, note: resolveNote.trim() })
              }
              disabled={resolveMutation.isPending || resolveNote.trim().length < 3}
            >
              {resolveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cerrar retiro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout HG Cash: transfiere el retiro YA aprobado al CBU/CVU del jugador */}
      <Dialog open={!!payoutPayment} onOpenChange={(o) => !o && setPayoutPayment(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>💸 Pagar retiro por HG Cash</DialogTitle>
            <DialogDescription>
              {payoutPayment && (
                <>
                  {payoutPayment.clientName || 'Cliente'} ·{' '}
                  <strong>{formatCurrency(payoutPayment.amount, payoutPayment.currency)}</strong>
                  <br />
                  La transferencia sale de la cuenta HG Cash. El jugador recibe aviso en el chat al acreditarse.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {(['cbu', 'cvu'] as const).map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={payoutTipo === t ? 'default' : 'outline'}
                  onClick={() => setPayoutTipo(t)}
                >
                  {t.toUpperCase()}
                </Button>
              ))}
            </div>
            <div>
              <Input
                placeholder={`${payoutTipo.toUpperCase()} (22 dígitos)`}
                value={payoutDestino}
                onChange={(e) => setPayoutDestino(e.target.value)}
                className="font-mono"
              />
              {payoutDestino && !/^\d{22}$/.test(payoutDestino.replace(/\D/g, '')) && (
                <p className="mt-1 text-[11px] text-amber-500">
                  ⚠️ No son 22 dígitos — si el jugador pasó un alias, conseguí el CBU/CVU real.
                </p>
              )}
            </div>
            <Input
              placeholder="Titular (opcional)"
              value={payoutTitular}
              onChange={(e) => setPayoutTitular(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutPayment(null)}>
              Cancelar
            </Button>
            <Button onClick={submitPayout} disabled={payoutMutation.isPending}>
              {payoutMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
